
import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from './use-toast';
import { generateDeviceCode } from '../utils/deviceCodeUtils';

interface DeviceActivationState {
  deviceCode: string;
  isLoading: boolean;
  isActivated: boolean;
  activatedAccount: string | null;
  activatedUserId: string | null;
  deviceRecordId: string | null; // UUID do dispositivo na tabela devices (para comandos remotos)
  availableScreens: any[];
  deviceSettings: {
    showClock: boolean;
    showNewsTicker: boolean;
  };
  associatedScreenId: string | null;
  hasActiveSubscription: boolean;
}

class DeviceActivationService {
  static saveDeviceCode(code: string) {
    sessionStorage.setItem('player_device_code', code);
  }

  static loadDeviceCode(): string | null {
    return sessionStorage.getItem('player_device_code');
  }
}

// usar util generateDeviceCode importado

export const useDeviceActivation = (externalDeviceCode?: string) => {
  const { toast } = useToast();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);
  const lastCheckedCodeRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);

  const [activationState, setActivationState] = useState<DeviceActivationState>({
    deviceCode: '',
    isLoading: true,
    isActivated: false,
    activatedAccount: null,
    activatedUserId: null,
    deviceRecordId: null,
    availableScreens: [],
    deviceSettings: {
      showClock: true,
      showNewsTicker: true
    },
    associatedScreenId: null,
    hasActiveSubscription: false
  });

  const checkActivation = useCallback(async () => {
    const currentCode = activationState.deviceCode || externalDeviceCode;

    console.log('🔍 [ATIVAÇÃO DEBUG] Estado atual antes da verificação:', {
      deviceCode: activationState.deviceCode,
      externalDeviceCode: externalDeviceCode,
      currentCode: currentCode,
      isActivated: activationState.isActivated
    });

    if (!currentCode) {
      console.log('🔍 [ATIVAÇÃO] Verificação abortada - código não disponível:', { currentCode });
      return;
    }

    if (isCheckingRef.current) {
      console.log('🔍 [ATIVAÇÃO] Verificação em andamento, aguardando...', { currentCode, isChecking: isCheckingRef.current });
      return;
    }

    // Verificar se já está ativado na sessão ANTES de fazer qualquer verificação no banco
    const sessionUserId = sessionStorage.getItem('player_activated_user_id');
    const sessionDeviceCode = sessionStorage.getItem('player_device_code');

    console.log('🔍 [ATIVAÇÃO DEBUG] Sessão atual:', {
      sessionUserId: sessionUserId,
      sessionDeviceCode: sessionDeviceCode,
      currentCode: currentCode,
      activationStateIsActivated: activationState.isActivated
    });

    // **MODIFICAÇÃO**: Permitir re-verificação se o estado não estiver ativo
    if (sessionUserId && sessionDeviceCode === currentCode && activationState.isActivated) {
      console.log('✅ [ATIVAÇÃO] Dispositivo já ativado na sessão, mantendo estado ativo');
      return;
    }

    // **MODIFICAÇÃO**: Permitir re-verificação se o estado não estiver ativo mesmo com código verificado
    if (lastCheckedCodeRef.current === currentCode && activationState.isActivated) {
      console.log('🔄 [ATIVAÇÃO] Evitando verificação duplicada para código já ativado:', currentCode);
      return;
    }

    console.log('🔍 [ATIVAÇÃO] INICIANDO verificação para código:', currentCode);
    isCheckingRef.current = true;
    lastCheckedCodeRef.current = currentCode;

    // **CORREÇÃO CRÍTICA**: Garantir que o código do dispositivo esteja no estado
    if (!activationState.deviceCode && externalDeviceCode) {
      console.log('🔄 [ATIVAÇÃO] Atualizando deviceCode no estado:', externalDeviceCode);
      setActivationState(prev => ({ ...prev, deviceCode: externalDeviceCode }));
    }

    // remover logs de segredos/keys

    try {
      setActivationState(prev => ({ ...prev, isLoading: true }));

      // Primeiro buscar o dispositivo na tabela devices
      const { data: deviceData, error: deviceError } = await supabase
        .from('devices')
        .select('*')
        .eq('code', currentCode)
        .maybeSingle();

      if (deviceError) {
        throw deviceError;
      }

      if (!deviceData) {
        // console.log('⚠️ [ATIVAÇÃO] Dispositivo não encontrado na tabela devices:', currentCode);

        // NOVA VERIFICAÇÃO: Checar se foi ativado pelo Companion na tabela device_pairings
        // console.log('🔍 [ATIVAÇÃO] Verificando tabela device_pairings...');
        const cleanCode = currentCode.replace(/-/g, '').toUpperCase();

        const { data: pairingData, error: pairingError } = await supabase
          .from('device_pairings')
          .select('*')
          .eq('pairing_code', cleanCode)
          .eq('status', 'active')
          .maybeSingle();

        if (pairingData && !pairingError && pairingData.driver_id) {
          console.log('✅ [ATIVAÇÃO] Dispositivo ativado pelo Companion! Driver:', pairingData.driver_id);

          // Ativar o dispositivo com os dados do motorista
          setActivationState(prev => ({
            ...prev,
            isLoading: false,
            isActivated: true,
            activatedAccount: pairingData.driver_id,
            activatedUserId: pairingData.driver_id,
            associatedScreenId: null, // Motoristas não têm screen_id específico
            hasActiveSubscription: true // Motoristas têm acesso liberado
          }));

          // Salvar na sessão
          sessionStorage.setItem('player_activated_user_id', pairingData.driver_id);
          sessionStorage.setItem('player_device_code', currentCode);
          sessionStorage.setItem('player_activated_by_companion', 'true');

          // Disparar evento de ativação
          window.dispatchEvent(new CustomEvent('playerActivated', {
            detail: { userId: pairingData.driver_id, screenId: null, activatedByCompanion: true }
          }));

          console.log('✅ [ATIVAÇÃO] Dispositivo ativado via Companion com sucesso!');
          isCheckingRef.current = false;
          return;
        }

        // Não encontrado em nenhuma tabela - LIMPAR SESSÃO ANTIGA
        // console.log('⚠️ [ATIVAÇÃO] Dispositivo não encontrado em nenhuma tabela');

        // Limpar qualquer sessão anterior (dispositivo foi removido do Companion)
        sessionStorage.removeItem('player_activated_user_id');
        sessionStorage.removeItem('player_activated_by_companion');
        localStorage.removeItem('activated_by_driver');

        setActivationState(prev => ({
          ...prev,
          isLoading: false,
          isActivated: false,
          activatedAccount: null,
          activatedUserId: null,
          associatedScreenId: null,
          hasActiveSubscription: false
        }));

        isCheckingRef.current = false;
        return;
      }


      console.log('📱 [ATIVAÇÃO] Dados do dispositivo:', {
        id: deviceData.id,
        code: deviceData.code,
        is_active: deviceData.is_active,
        userId: deviceData.user_id,
        screenId: deviceData.screen_id
      });

      if (!deviceData.is_active) {
        console.log('⚠️ [ATIVAÇÃO] Dispositivo não está ativo');
        setActivationState(prev => ({
          ...prev,
          isLoading: false,
          isActivated: false,
          activatedAccount: null,
          activatedUserId: null,
          associatedScreenId: null,
          hasActiveSubscription: false
        }));
        return;
      }

      console.log('✅ [ATIVAÇÃO DEBUG] Dispositivo está ativo, prosseguindo...');

      // **CORREÇÃO CRÍTICA**: Forçar ativação imediata se o dispositivo estiver ativo
      // mas o estado ainda não refletir isso
      if (deviceData.is_active && !activationState.isActivated) {
        console.log('🚨 [ATIVAÇÃO] DISPOSITIVO ATIVO MAS ESTADO NÃO ATUALIZADO!');
        console.log('🚨 [ATIVAÇÃO] Vai forçar ativação para:', currentCode);

        // **FORÇAR ATIVAÇÃO IMEDIATA** - pular verificações de perfil se necessário
        console.log('🚨 [ATIVAÇÃO] ATIVANDO DISPOSITIVO IMEDIATAMENTE!');

        // Atualizar estado imediatamente
        setActivationState(prev => ({
          ...prev,
          isLoading: false,
          isActivated: true,
          activatedAccount: deviceData.user_id,
          activatedUserId: deviceData.user_id,
          deviceRecordId: deviceData.id, // UUID do dispositivo na tabela devices
          associatedScreenId: deviceData.screen_id,
          hasActiveSubscription: true // Assumir assinatura ativa por enquanto
        }));

        // Salvar sessão
        sessionStorage.setItem('player_activated_user_id', deviceData.user_id);
        sessionStorage.setItem('player_device_code', currentCode);

        // Disparar evento para notificar useMedias
        window.dispatchEvent(new CustomEvent('playerActivated', {
          detail: { userId: deviceData.user_id, screenId: deviceData.screen_id }
        }));

        lastCheckedCodeRef.current = currentCode;
        isCheckingRef.current = false;

        console.log('✅ [ATIVAÇÃO] DISPOSITIVO ATIVADO FORÇADAMENTE!');
        return; // Sair da função após ativação forçada
      }

      // Depois buscar o perfil do usuário
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, full_name, subscription_status, subscription_plan, subscription_end_date')
        .eq('id', deviceData.user_id)
        .single();

      if (profileError || !profileData) {
        console.log('⚠️ [ATIVAÇÃO] Perfil do usuário não encontrado:', deviceData.user_id);
        setActivationState(prev => ({
          ...prev,
          isLoading: false,
          isActivated: false,
          activatedAccount: null,
          activatedUserId: null,
          associatedScreenId: null,
          hasActiveSubscription: false
        }));
        return;
      }

      console.log('✅ [ATIVAÇÃO DEBUG] Perfil do usuário encontrado:', profileData.id);

      // Verificar assinatura ativa
      const hasActiveSubscription = profileData &&
        profileData.subscription_status === 'active' &&
        profileData.subscription_end_date &&
        new Date(profileData.subscription_end_date) > new Date();

      console.log('💳 [ATIVAÇÃO] Status da assinatura:', {
        status: profileData?.subscription_status,
        endDate: profileData?.subscription_end_date,
        hasActive: hasActiveSubscription
      });

      console.log('✅ [ATIVAÇÃO DEBUG] Preparando para ativar dispositivo...');

      // **CORREÇÃO CRÍTICA**: Atualizar estado imediatamente antes de salvar sessão
      console.log('🔄 [ATIVAÇÃO DEBUG] Atualizando estado de ativação...');

      setActivationState(prev => {
        const newState = {
          ...prev,
          isLoading: false,
          isActivated: true,
          activatedAccount: deviceData.user_id,
          activatedUserId: deviceData.user_id,
          deviceRecordId: deviceData.id, // UUID do dispositivo na tabela devices
          associatedScreenId: deviceData.screen_id,
          hasActiveSubscription: hasActiveSubscription
        };

        console.log('🔄 [ATIVAÇÃO] Novo estado sendo definido:', {
          isActivated: newState.isActivated,
          associatedScreenId: newState.associatedScreenId,
          userId: newState.activatedUserId
        });

        return newState;
      });

      // Salvar dados na sessão
      sessionStorage.setItem('player_activated_user_id', deviceData.user_id);
      sessionStorage.setItem('player_device_code', currentCode);

      // Disparar evento para notificar useMedias
      window.dispatchEvent(new CustomEvent('playerActivated', {
        detail: { userId: deviceData.user_id, screenId: deviceData.screen_id }
      }));

      // Parar o intervalo de verificação
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      console.log('✅ [ATIVAÇÃO] Dispositivo ativado com sucesso!');

      lastCheckedCodeRef.current = currentCode;
      console.log('✅ [ATIVAÇÃO] Estado atualizado:', {
        isActivated: true,
        userId: deviceData.user_id,
        screenId: deviceData.screen_id
      });

    } catch (error) {
      console.error('❌ [ATIVAÇÃO] Erro inesperado:', error);
      setActivationState(prev => ({
        ...prev,
        isLoading: false,
        isActivated: false,
        activatedAccount: null,
        activatedUserId: null,
        associatedScreenId: null,
        hasActiveSubscription: false
      }));
    } finally {
      isCheckingRef.current = false;
      console.log('🔍 [ATIVAÇÃO DEBUG] Verificação finalizada, isCheckingRef:', isCheckingRef.current);
    }
  }, [activationState.deviceCode, externalDeviceCode]);

  const deactivateDevice = useCallback(() => {
    console.log('🔄 [ATIVAÇÃO] Desativando dispositivo...');

    // Limpar sessão
    sessionStorage.removeItem('player_activated_user_id');

    // Parar intervalo
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setActivationState(prev => ({
      ...prev,
      isActivated: false,
      activatedAccount: null,
      activatedUserId: null,
      availableScreens: [],
      associatedScreenId: null,
      hasActiveSubscription: false
    }));
  }, []);

  // Função para gerar um novo código de dispositivo
  const generateNewCode = useCallback(() => {
    const newCode = generateDeviceCode();
    console.log('🆕 Novo código gerado:', newCode);

    // Se estava ativado, desativa primeiro
    if (activationState.isActivated) {
      deactivateDevice();
    }

    setActivationState(prev => ({
      ...prev,
      deviceCode: newCode,
      isActivated: false,
      activatedAccount: null,
      activatedUserId: null,
      availableScreens: [],
      deviceSettings: {
        showClock: true,
        showNewsTicker: true
      },
      associatedScreenId: null,
      hasActiveSubscription: false
    }));

    DeviceActivationService.saveDeviceCode(newCode);
    lastCheckedCodeRef.current = null; // Reset para permitir nova verificação

    toast({
      title: "Novo código gerado",
      description: `Use este código no dashboard: ${newCode}`,
    });
  }, [activationState.isActivated, deactivateDevice, toast]);

  // Inicialização única do dispositivo
  useEffect(() => {
    if (isInitializedRef.current) return;

    let deviceCode = externalDeviceCode || DeviceActivationService.loadDeviceCode();
    if (!deviceCode) {
      deviceCode = generateDeviceCode();
      DeviceActivationService.saveDeviceCode(deviceCode);
    }

    console.log('🔍 [ATIVAÇÃO] Inicializando com deviceCode:', deviceCode);
    console.log('🔍 [ATIVAÇÃO] externalDeviceCode:', externalDeviceCode);

    setActivationState(prev => ({
      ...prev,
      deviceCode: externalDeviceCode || deviceCode,
      isLoading: true
    }));

    // Limpar qualquer estado de sessão anterior se o código mudou
    const currentSessionCode = sessionStorage.getItem('player_device_code');
    if (currentSessionCode && currentSessionCode !== deviceCode) {
      console.log('🔄 [ATIVAÇÃO] Código mudou, limpando sessão anterior');
      sessionStorage.removeItem('player_activated_user_id');
    }

    // Salvar o novo código na sessão
    sessionStorage.setItem('player_device_code', deviceCode);

    isInitializedRef.current = true;

    // Verificar ativação após inicialização
    setTimeout(() => {
      checkActivation();
    }, 100);

  }, [externalDeviceCode]); // Apenas externalDeviceCode como dependência

  // Configurar verificação periódica apenas quando necessário
  useEffect(() => {
    // Obter código atual (priorizar external, depois state, depois storage)
    const currentCode = externalDeviceCode || activationState.deviceCode || DeviceActivationService.loadDeviceCode();

    // Só configurar intervalo se:
    // 1. Dispositivo foi inicializado
    // 2. Tem código de dispositivo
    // 3. Não está ativado
    // 4. Não há intervalo ativo
    if (!isInitializedRef.current ||
      !currentCode ||
      activationState.isActivated ||
      intervalRef.current) {
      console.log('🔍 [ATIVAÇÃO] Não configurando intervalo:', {
        initialized: isInitializedRef.current,
        hasCode: !!currentCode,
        isActivated: activationState.isActivated,
        hasInterval: !!intervalRef.current,
        currentCode
      });
      return;
    }

    console.log('🔍 [ATIVAÇÃO] Configurando verificação periódica para código:', currentCode);

    // Configurar intervalo para verificações periódicas
    intervalRef.current = setInterval(async () => {
      // Se já foi ativado, parar o intervalo
      if (activationState.isActivated) {
        console.log('✅ [ATIVAÇÃO] Dispositivo já ativado, parando intervalo');
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      console.log('🔍 [ATIVAÇÃO] Verificação periódica para código:', currentCode);
      await checkActivation();
    }, 5000); // 5 segundos

    return () => {
      console.log('🧹 [ATIVAÇÃO] Limpando intervalo de verificação');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [activationState.deviceCode, activationState.isActivated, externalDeviceCode]); // Incluir todas as dependências relevantes

  // =============================================================================
  // LISTENER EM TEMPO REAL + POLLING - Detectar desconexão pelo Companion
  // Monitora mudanças na tabela device_pairings para desativar o player
  // quando o motorista desconectar pelo Companion
  // Usa Realtime + Polling como backup para garantir funcionamento
  // =============================================================================
  useEffect(() => {
    // Só monitorar se estiver ativado via Companion
    const isCompanionActivated = sessionStorage.getItem('player_activated_by_companion') === 'true';
    const currentCode = activationState.deviceCode || externalDeviceCode;

    if (!activationState.isActivated || !isCompanionActivated || !currentCode) {
      return;
    }

    const cleanCode = currentCode.replace(/-/g, '').toUpperCase();
    console.log('📡 [ATIVAÇÃO] Iniciando monitoramento em tempo real para:', cleanCode);

    // Função para desconectar o player
    const disconnectPlayer = () => {
      console.log('⚠️ [ATIVAÇÃO] Pareamento desativado pelo Companion! Desconectando player...');

      // Limpar sessão
      sessionStorage.removeItem('player_activated_user_id');
      sessionStorage.removeItem('player_activated_by_companion');
      sessionStorage.removeItem('player_device_code');

      console.log('✅ [ATIVAÇÃO] Player desconectado com sucesso! Recarregando página...');

      // Forçar reload da página para garantir que a UI atualize
      window.location.reload();
    };

    // MÉTODO 1: Realtime (mais rápido, mas depende da configuração do Supabase)
    const channel = supabase
      .channel(`pairing-monitor-${cleanCode}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'device_pairings',
          filter: `pairing_code=eq.${cleanCode}`
        },
        (payload) => {
          console.log('📡 [ATIVAÇÃO] Mudança detectada no pareamento via Realtime:', payload);

          const newData = payload.new as any;

          // Se status mudou para inactive ou driver_id foi removido
          if (newData.status === 'inactive' || newData.status === 'pending' || newData.driver_id === null) {
            disconnectPlayer();
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 [ATIVAÇÃO] Status do canal Realtime:', status);
      });

    // MÉTODO 2: Polling como backup (verifica a cada 5 segundos)
    const pollingInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('device_pairings')
          .select('status, driver_id')
          .eq('pairing_code', cleanCode)
          .single();

        if (error) {
          console.log('⚠️ [ATIVAÇÃO] Erro ao verificar pareamento (polling):', error);
          return;
        }

        // Se não encontrou ou status é inactive/pending ou driver_id é null
        if (!data || data.status === 'inactive' || data.status === 'pending' || data.driver_id === null) {
          console.log('📡 [ATIVAÇÃO] Pareamento inativo detectado via polling:', data);
          disconnectPlayer();
        }
      } catch (err) {
        console.error('❌ [ATIVAÇÃO] Erro no polling:', err);
      }
    }, 5000); // Verificar a cada 5 segundos

    // Cleanup ao desmontar
    return () => {
      console.log('🧹 [ATIVAÇÃO] Removendo monitoramento');
      supabase.removeChannel(channel);
      clearInterval(pollingInterval);
    };
  }, [activationState.isActivated, activationState.deviceCode, externalDeviceCode]);

  // Limpar recursos quando componente for desmontado
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      isCheckingRef.current = false;
    };
  }, []);

  // Retornar o estado e as funções
  return {
    ...activationState,
    checkActivation,
    generateNewCode,
  };
};
